import React, { useState, useEffect } from 'react';
import { Card, Typography, Table, Button, Modal, Form, Select, Input, message, Popconfirm, Space } from 'antd';
import { EditOutlined, DeleteOutlined, CheckCircleOutlined, PlusOutlined } from '@ant-design/icons';
import examSessionService from '../../services/examSessionService';
import courseService from '../../services/courseService';
import * as subjectService from '../../services/subjectService';
import lopHocPhanService from '../../services/lopHocPhanService';
import phieuChamService from '../../services/phieuChamService';
import type { ExamSession, Course, Subject, LopHocPhan, PhieuCham } from '../../../../shared/types';
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;

const ExamSessionManagementPage: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [examSessions, setExamSessions] = useState<ExamSession[]>([]);
  const [khoaHocList, setKhoaHocList] = useState<Course[]>([]);
  const [monHocList, setMonHocList] = useState<Subject[]>([]);
  const [lopHPList, setLopHPList] = useState<LopHocPhan[]>([]);
  const [phieuChamList, setPhieuChamList] = useState<PhieuCham[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<ExamSession | null>(null);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await examSessionService.getExamSessions();
      setExamSessions(res.data || []);
    } catch (error) { message.error('Lỗi tải dữ liệu'); } finally { setLoading(false); }
  };
  
  const fetchKhoaHoc = async () => {
    try {
      const res = await courseService.getCourses({ page: 1, limit: 1000 });
      setKhoaHocList(res.data || []);
    } catch (error) { message.error('Lỗi tải khóa học'); }
  };
  
  const fetchPhieuCham = async () => {
    try {
      const res = await phieuChamService.getPhieuChamList();
      setPhieuChamList(res.data || []);
    } catch (error) { message.error('Lỗi tải phiếu chấm'); }
  };
  
  const fetchMonHocByKhoa = async (khoaHocId: string) => {
    if (!khoaHocId) { setMonHocList([]); return; }
    try {
      const res = await subjectService.listSubjects({ khoaHocId: khoaHocId, limit: 1000 });
      setMonHocList(res.data || []);
    } catch (error) { message.error("Lỗi tải môn học"); }
  };

  const fetchLopHPByMon = async (monHocId: string) => {
    if (!monHocId) { setLopHPList([]); return; }
    try {
      const res = await lopHocPhanService.getLopHocPhanByMonHoc(monHocId);
      setLopHPList(res.data || []);
    } catch (error) { message.error("Lỗi tải lớp học phần"); }
  };

  const fetchSubjects = async () => {
    try {
      const res = await subjectService.listSubjects({ limit: 1000 });
      setSubjects(res.data || []);
    } catch (error) { message.error("Lỗi tải môn học"); }
  };

  useEffect(() => {
    fetchData();
    fetchKhoaHoc();
    fetchPhieuCham();
    fetchSubjects();
  }, []);

  const handleAdd = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const handleEdit = async (record: ExamSession) => {
    setEditing(record);
    
    // Find KhoaHocID from the full subjects list using MonHocID
    const subject = subjects.find(s => s.MonHocID === (record.MonHocID || record.MaMonHoc));
    const khoaHocId = subject?.KhoaHocID;

    if (khoaHocId) {
      await fetchMonHocByKhoa(khoaHocId);
      const monHocId = record.MonHocID || record.MaMonHoc;
      if (monHocId) {
        await fetchLopHPByMon(monHocId);
      }
    }
    
    form.setFieldsValue({ ...record, KhoaHocID: khoaHocId });
    setModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await examSessionService.deleteExamSession(id);
      message.success('Xóa thành công');
      fetchData();
    } catch (error) { message.error('Lỗi khi xóa'); }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editing) {
        const examSessionId = editing.ExamSessionID || editing.KyThiID;
        await examSessionService.updateExamSession(examSessionId, values);
      } else {
        await examSessionService.createExamSession(values);
      }
      message.success(editing ? 'Cập nhật thành công' : 'Tạo mới thành công');
      setModalOpen(false);
      fetchData();
    } catch (error) { message.error('Lỗi khi lưu'); }
  };

  const columns = [
    { title: 'Tên kỳ thi/phiên chấm', dataIndex: 'TenKyThi', key: 'TenKyThi' },
    { 
      title: 'Lớp học phần', 
      dataIndex: 'TenLopHP', 
      key: 'TenLopHP',
      render: (text: string, record: ExamSession) => text || record.LopHPID || record.MaLHP
    },
    { 
      title: 'Môn học', 
      dataIndex: 'TenMonHoc', 
      key: 'TenMonHoc',
      render: (text: string, record: ExamSession) => text || record.MonHocID || record.MaMonHoc
    },
    { 
      title: 'Phiếu chấm mẫu', 
      dataIndex: 'TenPhieuCham', 
      key: 'TenPhieuCham',
      render: (text: string, record: ExamSession) => text || record.PhieuChamID
    },
    {
      title: 'Hành động',
      key: 'actions',
      render: (_: any, record: ExamSession) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>Sửa</Button>
          <Popconfirm title="Xóa kỳ thi này?" onConfirm={() => handleDelete(record.ExamSessionID || record.KyThiID)}>
            <Button size="small" danger icon={<DeleteOutlined />}>Xóa</Button>
          </Popconfirm>
          <Button 
            size="small" 
            type="primary" 
            icon={<CheckCircleOutlined />} 
            onClick={() => navigate(`/cham-diem/${record.ExamSessionID || record.KyThiID}`)}
          >
            Chấm điểm
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Title level={2}>Danh sách Kỳ thi/Phiên chấm</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} style={{ marginBottom: 16 }}>
          Tạo kỳ thi/phiên chấm mới
        </Button>
        <Table rowKey="ExamSessionID" loading={loading} dataSource={examSessions} columns={columns} bordered />
        <Modal
          open={modalOpen}
          title={editing ? "Sửa kỳ thi/phiên chấm" : "Tạo kỳ thi/phiên chấm mới"}
          onOk={handleModalOk}
          onCancel={() => setModalOpen(false)}
          okText={editing ? "Cập nhật" : "Tạo mới"}
          cancelText="Hủy"
        >
          <Form form={form} layout="vertical">
            <Form.Item name="KhoaHocID" label="Khóa học" rules={[{ required: true, message: 'Chọn khóa học' }]}> 
              <Select
                options={khoaHocList.map(k => ({ label: k.TenKhoaHoc, value: k.KhoaHocID }))}
                placeholder="Chọn khóa học"
                onChange={async (v) => {
                  form.setFieldsValue({ MonHocID: undefined, LopHPID: undefined });
                  await fetchMonHocByKhoa(v);
                  setLopHPList([]);
                }}
              />
            </Form.Item>
            <Form.Item name="MonHocID" label="Môn học" rules={[{ required: true, message: 'Chọn môn học' }]}> 
              <Select
                options={monHocList.map(m => ({ label: m.TenMonHoc, value: m.MonHocID }))}
                placeholder="Chọn môn học"
                onChange={async (v) => {
                  form.setFieldsValue({ LopHPID: undefined });
                  await fetchLopHPByMon(v);
                }}
                disabled={!form.getFieldValue('KhoaHocID')}
              />
            </Form.Item>
            <Form.Item name="LopHPID" label="Lớp học phần" rules={[{ required: true, message: 'Chọn lớp học phần' }]}> 
              <Select
                options={lopHPList.map(l => ({ label: l.TenLHP, value: l.MaLHP }))}
                placeholder="Chọn lớp học phần"
                disabled={!form.getFieldValue('MonHocID')}
              />
            </Form.Item>
            <Form.Item name="TenKyThi" label="Tên kỳ thi/phiên chấm" rules={[{ required: true, message: 'Nhập tên kỳ thi/phiên chấm' }]}> 
              <Input placeholder="Nhập tên kỳ thi hoặc phiên chấm" />
            </Form.Item>
            <Form.Item name="PhieuChamID" label="Phiếu chấm mẫu" rules={[{ required: true, message: 'Chọn phiếu chấm mẫu' }]}> 
              <Select options={phieuChamList.map(p => ({ label: p.TenPhieu, value: p.PhieuID }))} placeholder="Chọn phiếu chấm mẫu" />
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default ExamSessionManagementPage; 